import { useEffect, useState } from 'react';
import { apiGet, apiPut, ApiError } from '../../api/client.js';

interface TeamleiderOptie {
  id: string;
  displayName: string;
}

/**
 * Zelf-service teamleiderkeuze (besluit Jos 28 aug 2026). De keuze staat op
 * het User-record en wordt als snapshot meegenomen bij elke nieuwe declaratie;
 * het admin-paneel van Linda blijft ernaast bestaan als correctiemiddel.
 */
export function TeamleiderKiezer(): JSX.Element | null {
  const [opties, setOpties] = useState<TeamleiderOptie[]>([]);
  const [huidige, setHuidige] = useState<string>('');
  const [geladen, setGeladen] = useState(false);
  const [bezig, setBezig] = useState(false);
  const [melding, setMelding] = useState<string | null>(null);

  useEffect(() => {
    void apiGet<{ teamleiders: TeamleiderOptie[]; huidigeTeamleiderUserId: string | null }>(
      '/api/v1/declaraties/teamleiders',
    )
      .then((d) => {
        setOpties(d.teamleiders);
        setHuidige(d.huidigeTeamleiderUserId ?? '');
        setGeladen(true);
      })
      .catch(() => undefined);
  }, []);

  async function kies(teamleiderUserId: string): Promise<void> {
    const vorige = huidige;
    setHuidige(teamleiderUserId);
    setBezig(true);
    setMelding(null);
    try {
      await apiPut('/api/v1/declaraties/teamleider', {
        teamleiderUserId: teamleiderUserId || null,
      });
      setMelding('Opgeslagen. Je nieuwe declaraties gaan naar deze teamleider.');
    } catch (err) {
      setHuidige(vorige);
      setMelding(
        err instanceof ApiError ? err.message : 'Opslaan is mislukt. Probeer het opnieuw.',
      );
    } finally {
      setBezig(false);
    }
  }

  if (!geladen) return null;

  return (
    <div className="mt-4 rounded-2xl border border-brand-100 bg-white p-5 sm:p-6">
      <div className="flex flex-wrap items-center gap-3">
        <label htmlFor="teamleider" className="text-[14px] font-semibold text-ink-900">
          Mijn teamleider
        </label>
        <select
          id="teamleider"
          value={huidige}
          disabled={bezig}
          onChange={(e) => void kies(e.target.value)}
          className="rounded-lg border border-brand-100 bg-brand-50/50 px-3 py-2 text-[14px] text-ink-900 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400 disabled:opacity-50"
        >
          <option value="">Nog geen teamleider gekozen</option>
          {opties.map((o) => (
            <option key={o.id} value={o.id}>
              {o.displayName}
            </option>
          ))}
        </select>
      </div>
      <p className="mt-2 text-[13px] text-ink-900/60">
        Je teamleider geeft als eerste akkoord op je declaraties. Staat je teamleider er niet
        tussen, of kies je niemand? Dan beoordeelt kantoor je declaraties.
      </p>
      {melding && <p className="mt-2 text-[13px] text-ink-900/70">{melding}</p>}
    </div>
  );
}
